import { useEffect, useState, useContext, useRef } from 'react';
import { UserContext } from '../../contexts/UserContext';
import {
	Box,
	Card,
	CardContent,
	Checkbox,
	Chip,
	CircularProgress,
	Collapse,
	Divider,
	Drawer,
	FormControl,
	IconButton,
	InputAdornment,
	MenuItem,
	Paper,
	Select,
	Tabs,
	Tab,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
	Button,
	alpha,
	useTheme,
} from '@mui/material';
import {
	ChevronLeftOutlined,
	ChevronRightOutlined,
	RefreshOutlined,
	AccessTimeOutlined,
	CalendarMonthOutlined,
	ViewListOutlined,
	ExpandMore,
	ExpandLess,
	LockOpenOutlined,
	CloseOutlined,
	PersonOutlined,
	SearchOutlined,
	SelectAllOutlined,
	DeselectOutlined,
} from '@mui/icons-material';
import { generateClient, get, put } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getContact } from 'src/graphql/queries';
import { ActivityLog } from '../contacts/activityLog';

export default function CallBacks(props) {
	const client = generateClient();
	const userContext = useContext(UserContext);
	const theme = useTheme();

	const [callBacks, setCallBacks] = useState(null);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [selectedCallback, setSelectedCallback] = useState(null);
	const [contactData, setContactData] = useState(null);
	const [contactTab, setContactTab] = useState(0);
	const [releasing, setReleasing] = useState(false);
	const [bulkReleasing, setBulkReleasing] = useState(false);
	const [weekOffset, setWeekOffset] = useState(0);
	const [viewMode, setViewMode] = useState(() => localStorage.getItem('fm-callbacks-view-mode') || 'list');
	const [expandedSections, setExpandedSections] = useState({ overdue: true, ready: true, upcoming: true });

	// Search & Filters
	const [searchQuery, setSearchQuery] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [assignmentFilter, setAssignmentFilter] = useState('all');

	// Bulk selection
	const [selectedIds, setSelectedIds] = useState(new Set());

	const interval = useRef(null);

	function handleViewModeChange(_, newMode) {
		if (newMode) {
			setViewMode(newMode);
			localStorage.setItem('fm-callbacks-view-mode', newMode);
		}
	}

	function toggleSection(section) {
		setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
	}

	// Fetch callbacks when drawer opens
	useEffect(() => {
		if (props.campaignId && props.open) {
			fetchCallbacks();
			interval.current = setInterval(fetchCallbacks, 60000);
		}
		return () => {
			if (interval.current) clearInterval(interval.current);
		};
	}, [props.campaignId, props.open]);

	// Fetch contact details when a callback is selected
	useEffect(() => {
		if (selectedCallback) {
			fetchContactData(selectedCallback.contactId);
		} else {
			setContactData(null);
		}
	}, [selectedCallback]);

	// Reset state when drawer closes
	useEffect(() => {
		if (!props.open) {
			setSelectedCallback(null);
			setContactData(null);
			setContactTab(0);
		}
	}, [props.open]);

	async function fetchCallbacks() {
		try {
			setRefreshing(true);
			const response = await get({
				apiName: 'cdyxoutreach',
				path: `/cache/callBacks/${props.campaignId}`,
				options: {
					headers: {
						Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
				},
			}).response;

			const result = await response.body.json();
			let list = [];
			if (result.data) {
				if (Array.isArray(result.data)) {
					list = result.data;
				} else {
					for (const key in result.data) {
						list.push(JSON.parse(result.data[key]));
					}
				}
			}

			list.sort((a, b) => new Date(a.callDateTime) - new Date(b.callDateTime));
			setCallBacks(list);
		} catch (error) {
			console.error('Error getting callbacks', error);
		}
		setLoading(false);
		setRefreshing(false);
	}

	async function fetchContactData(contactId) {
		try {
			const response = await client.graphql({
				query: getContact,
				variables: { id: contactId },
			});
			const contact = response.data.getContact;
			if (contact && contact.customFields) {
				try {
					const parsed = JSON.parse(contact.customFields);
					contact.customFieldsList = [];
					for (const field in parsed) {
						contact.customFieldsList.push({ key: field, value: parsed[field] });
					}
					contact.customFields = parsed;
				} catch (e) {
					contact.customFields = null;
					contact.customFieldsList = [];
				}
			}
			setContactData(contact);
		} catch (error) {
			console.error('Error fetching contact', error);
			setContactData(null);
		}
	}

	async function releaseCallBack(callback) {
		setReleasing(true);
		try {
			await put({
				apiName: 'cdyxoutreach',
				path: `/cache/updateCallback/${callback.campaignId}/${callback.contactId}/`,
				options: {
					headers: {
						Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					body: {
						agentId: null,
					},
				},
			}).response;
			fetchCallbacks();
		} catch (error) {
			console.error('Error releasing callback', error);
		}
		setReleasing(false);
	}

	async function bulkRelease() {
		if (selectedIds.size === 0) return;
		setBulkReleasing(true);
		try {
			const session = await fetchAuthSession();
			const headers = {
				Authorization: `Bearer ${session.tokens.idToken}`,
				'x-api-key': userContext.apiKey,
			};
			const promises = Array.from(selectedIds).map((id) => {
				const cb = callbacks.find((c) => getCallbackKey(c) === id);
				if (!cb || !cb.agentId) return Promise.resolve();
				return put({
					apiName: 'cdyxoutreach',
					path: `/cache/updateCallback/${cb.campaignId}/${cb.contactId}/`,
					options: { headers, body: { agentId: null } },
				}).response;
			});
			await Promise.all(promises);
			setSelectedIds(new Set());
			fetchCallbacks();
		} catch (error) {
			console.error('Error bulk releasing callbacks', error);
		}
		setBulkReleasing(false);
	}

	function getCallbackKey(cb) {
		return `${cb.contactId}:${cb.callDateTime}`;
	}

	function toggleSelect(cb) {
		const key = getCallbackKey(cb);
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	}

	function selectAllFiltered() {
		const assignedFiltered = filteredCallbacks.filter((cb) => cb.agentId);
		const keys = assignedFiltered.map(getCallbackKey);
		setSelectedIds(new Set(keys));
	}

	function deselectAll() {
		setSelectedIds(new Set());
	}

	function getStatusInfo(callback) {
		const now = new Date();
		const callbackTime = new Date(callback.callDateTime);
		const diffMinutes = (callbackTime - now) / 60000;

		if (diffMinutes <= -1440) {
			return { label: 'Overdue', color: 'error', variant: 'filled' };
		} else if (diffMinutes <= 60) {
			return { label: 'Ready', color: 'success', variant: 'filled' };
		} else {
			return { label: 'Upcoming', color: 'info', variant: 'outlined' };
		}
	}

	// ─── Derived data ───────────────────────────────────────────────────────────

	const callbacks = callBacks || [];

	// Apply search and filters
	const filteredCallbacks = callbacks.filter((cb) => {
		// Search filter
		if (searchQuery) {
			const q = searchQuery.toLowerCase();
			const matchesPhone = cb.callBackPOC?.toLowerCase().includes(q);
			const matchesAgent = cb.createdByAgentName?.toLowerCase().includes(q) || cb.agentId?.toLowerCase().includes(q);
			if (!matchesPhone && !matchesAgent) return false;
		}

		// Status filter
		if (statusFilter !== 'all') {
			const status = getStatusInfo(cb);
			if (statusFilter === 'overdue' && status.label !== 'Overdue') return false;
			if (statusFilter === 'due' && status.label !== 'Ready') return false;
			if (statusFilter === 'upcoming' && status.label !== 'Upcoming') return false;
		}

		// Assignment filter
		if (assignmentFilter === 'assigned' && !cb.agentId) return false;
		if (assignmentFilter === 'unassigned' && cb.agentId) return false;

		return true;
	});

	// List view groupings (use filtered list)
	const now = new Date();
	const overdueCallbacks = filteredCallbacks.filter((cb) => (new Date(cb.callDateTime) - now) / 60000 <= -1440);
	const readyCallbacks = filteredCallbacks.filter((cb) => {
		const diff = (new Date(cb.callDateTime) - now) / 60000;
		return diff > -1440 && diff <= 60;
	});
	const upcomingCallbacks = filteredCallbacks.filter((cb) => (new Date(cb.callDateTime) - now) / 60000 > 60);

	// Bulk selection counts
	const selectedAssignedCount = Array.from(selectedIds).filter((id) => {
		const cb = callbacks.find((c) => getCallbackKey(c) === id);
		return cb?.agentId;
	}).length;

	// Calendar view helpers
	const today = new Date();
	const dayOfWeek = today.getDay();
	const monday = new Date(today);
	monday.setDate(today.getDate() - ((dayOfWeek === 0 ? 7 : dayOfWeek) - 1) + weekOffset * 7);
	monday.setHours(0, 0, 0, 0);

	const weekDays = Array.from({ length: 7 }, (_, i) => {
		const d = new Date(monday);
		d.setDate(monday.getDate() + i);
		return d;
	});

	const callbacksByDay = weekDays.map((day) => {
		const dayStart = new Date(day);
		dayStart.setHours(0, 0, 0, 0);
		const dayEnd = new Date(day);
		dayEnd.setHours(23, 59, 59, 999);
		return filteredCallbacks.filter((cb) => {
			const cbDate = new Date(cb.callDateTime);
			return cbDate >= dayStart && cbDate <= dayEnd;
		});
	});

	const isToday = (date) => {
		const t = new Date();
		return date.getDate() === t.getDate() && date.getMonth() === t.getMonth() && date.getFullYear() === t.getFullYear();
	};

	const weekCount = callbacksByDay.reduce((sum, day) => sum + day.length, 0);

	const todayStart = new Date();
	todayStart.setHours(0, 0, 0, 0);
	const todayEnd = new Date();
	todayEnd.setHours(23, 59, 59, 999);
	const todayCount = filteredCallbacks.filter((cb) => {
		const d = new Date(cb.callDateTime);
		return d >= todayStart && d <= todayEnd;
	}).length;

	const sunday = new Date(monday);
	sunday.setDate(monday.getDate() + 6);
	const weekLabel = `${monday.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${sunday.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;

	// ─── Render ─────────────────────────────────────────────────────────────────

	return (
		<Drawer
			sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 520, md: 600 } } }}
			anchor="right"
			open={props.open}
			onClose={props.onClose}
		>
			<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				{/* Header */}
				<Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
					<Typography variant="h6" fontWeight={600}>
						Callbacks — {props.campaignName}
					</Typography>
					<IconButton onClick={props.onClose} size="small">
						<CloseOutlined fontSize="small" />
					</IconButton>
				</Box>

				{/* Content */}
				<Box sx={{ flexGrow: 1, overflow: 'auto', px: 2.5, py: 2 }}>
					{loading && (
						<Box display="flex" justifyContent="center" alignItems="center" py={6}>
							<CircularProgress />
						</Box>
					)}

					{!loading && (
						<Box display="flex" flexDirection="column" gap={1.5}>
							{/* Top bar: counts + view toggle + refresh */}
							<Box display="flex" alignItems="center" gap={1.5}>
								<Typography variant="caption" color="text.secondary">
									<Box component="span" fontWeight={700} color="text.primary">{todayCount}</Box> today
								</Typography>
								<Typography variant="caption" color="text.secondary">
									<Box component="span" fontWeight={700} color="text.primary">{weekCount}</Box> this week
								</Typography>
								<Typography variant="caption" color="text.secondary">
									<Box component="span" fontWeight={700} color="text.primary">{filteredCallbacks.length}</Box>{callbacks.length !== filteredCallbacks.length ? ` / ${callbacks.length}` : ''} total
								</Typography>
								<Box flex={1} />
								<ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small" sx={{ height: 28 }}>
									<ToggleButton value="calendar" sx={{ px: 1, py: 0 }}>
										<CalendarMonthOutlined sx={{ fontSize: 16 }} />
									</ToggleButton>
									<ToggleButton value="list" sx={{ px: 1, py: 0 }}>
										<ViewListOutlined sx={{ fontSize: 16 }} />
									</ToggleButton>
								</ToggleButtonGroup>
								<Tooltip title="Refresh">
									<IconButton onClick={fetchCallbacks} disabled={refreshing} size="small">
										{refreshing ? <CircularProgress size={14} /> : <RefreshOutlined sx={{ fontSize: 16 }} />}
									</IconButton>
								</Tooltip>
							</Box>

							{/* Search & Filters */}
							<Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
								<TextField
									size="small"
									placeholder="Search phone or agent..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
											</InputAdornment>
										),
									}}
									sx={{ flex: 1, minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: '8px', height: 34 } }}
								/>
								<FormControl size="small" sx={{ minWidth: 110 }}>
									<Select
										value={statusFilter}
										onChange={(e) => setStatusFilter(e.target.value)}
										sx={{ borderRadius: '8px', height: 34, fontSize: '0.8rem' }}
									>
										<MenuItem value="all">All Status</MenuItem>
										<MenuItem value="overdue">Overdue</MenuItem>
										<MenuItem value="due">Ready</MenuItem>
										<MenuItem value="upcoming">Upcoming</MenuItem>
									</Select>
								</FormControl>
								<FormControl size="small" sx={{ minWidth: 120 }}>
									<Select
										value={assignmentFilter}
										onChange={(e) => setAssignmentFilter(e.target.value)}
										sx={{ borderRadius: '8px', height: 34, fontSize: '0.8rem' }}
									>
										<MenuItem value="all">All Agents</MenuItem>
										<MenuItem value="assigned">Assigned</MenuItem>
										<MenuItem value="unassigned">Unassigned</MenuItem>
									</Select>
								</FormControl>
							</Box>

							{/* Bulk actions bar */}
							{viewMode === 'list' && (
								<Box display="flex" alignItems="center" gap={1}>
									<Tooltip title="Select all assigned">
										<IconButton size="small" onClick={selectAllFiltered}>
											<SelectAllOutlined sx={{ fontSize: 16 }} />
										</IconButton>
									</Tooltip>
									{selectedIds.size > 0 && (
										<Tooltip title="Deselect all">
											<IconButton size="small" onClick={deselectAll}>
												<DeselectOutlined sx={{ fontSize: 16 }} />
											</IconButton>
										</Tooltip>
									)}
									{selectedIds.size > 0 && (
										<>
											<Typography variant="caption" color="text.secondary">
												{selectedIds.size} selected
											</Typography>
											<Button
												variant="outlined"
												size="small"
												startIcon={bulkReleasing ? <CircularProgress size={14} /> : <LockOpenOutlined sx={{ fontSize: 16 }} />}
												disabled={bulkReleasing || selectedAssignedCount === 0}
												onClick={bulkRelease}
												sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, ml: 'auto' }}
											>
												{bulkReleasing ? 'Releasing...' : `Release ${selectedAssignedCount}`}
											</Button>
										</>
									)}
								</Box>
							)}

							{/* ─── Calendar View ─── */}
							{viewMode === 'calendar' && (
								<>
									{/* Week navigation */}
									<Box display="flex" alignItems="center" justifyContent="space-between">
										<IconButton size="small" onClick={() => setWeekOffset((prev) => prev - 1)}>
											<ChevronLeftOutlined fontSize="small" />
										</IconButton>
										<Typography variant="caption" fontWeight={600} color="text.primary">
											{weekLabel}
										</Typography>
										<IconButton size="small" onClick={() => setWeekOffset((prev) => prev + 1)}>
											<ChevronRightOutlined fontSize="small" />
										</IconButton>
									</Box>

									{/* Weekly calendar grid */}
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: 'repeat(7, 1fr)',
											gap: 0.5,
											border: '1px solid',
											borderColor: 'divider',
											borderRadius: '10px',
											overflow: 'hidden',
										}}
									>
										{/* Day headers */}
										{weekDays.map((day, i) => (
											<Box
												key={`header-${i}`}
												sx={{
													py: 0.75,
													textAlign: 'center',
													bgcolor: isToday(day) ? 'primary.main' : 'action.hover',
													borderBottom: '1px solid',
													borderColor: 'divider',
												}}
											>
												<Typography
													variant="caption"
													fontWeight={700}
													color={isToday(day) ? 'primary.contrastText' : 'text.secondary'}
													sx={{ fontSize: '0.65rem', textTransform: 'uppercase' }}
												>
													{day.toLocaleDateString(undefined, { weekday: 'short' })}
												</Typography>
												<Typography
													variant="caption"
													display="block"
													fontWeight={600}
													color={isToday(day) ? 'primary.contrastText' : 'text.primary'}
													sx={{ fontSize: '0.7rem' }}
												>
													{day.getDate()}
												</Typography>
											</Box>
										))}

										{/* Day columns with callback slots */}
										{weekDays.map((day, i) => (
											<Box
												key={`col-${i}`}
												sx={{
													minHeight: 80,
													p: 0.5,
													display: 'flex',
													flexDirection: 'column',
													gap: 0.5,
													bgcolor: isToday(day) ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
												}}
											>
												{callbacksByDay[i].map((callback, idx) => {
													const status = getStatusInfo(callback);
													const isSelected = selectedCallback?.contactId === callback.contactId && selectedCallback?.callDateTime === callback.callDateTime;

													return (
														<Box
															key={`${callback.contactId}-${idx}`}
															onClick={(e) => {
																e.stopPropagation();
																setSelectedCallback(isSelected ? null : callback);
																setContactTab(0);
															}}
															sx={{
																px: 0.75,
																py: 0.5,
																borderRadius: '6px',
																cursor: 'pointer',
																border: '1px solid',
																borderColor: isSelected ? 'primary.main' : 'transparent',
																bgcolor:
																	status.label === 'Overdue'
																		? alpha(theme.palette.error.main, 0.08)
																		: status.label === 'Ready'
																			? alpha(theme.palette.success.main, 0.08)
																			: alpha(theme.palette.info.main, 0.06),
																transition: 'all 0.15s ease',
																'&:hover': {
																	bgcolor: alpha(theme.palette.primary.main, 0.1),
																	borderColor: 'primary.light',
																},
															}}
														>
															<Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.65rem', lineHeight: 1.3 }}>
																{new Date(callback.callDateTime).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
															</Typography>
															<Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ fontSize: '0.6rem', lineHeight: 1.2 }}>
																{callback.callBackPOC || 'No #'}
															</Typography>
														</Box>
													);
												})}
											</Box>
										))}
									</Box>
								</>
							)}

							{/* ─── List View ─── */}
							{viewMode === 'list' && (
								<Box display="flex" flexDirection="column" gap={1}>
									{(statusFilter === 'all' || statusFilter === 'overdue') && (
										<CallbackSection
											title="Overdue"
											count={overdueCallbacks.length}
											color="error.main"
											expanded={expandedSections.overdue}
											onToggle={() => toggleSection('overdue')}
											callbacks={overdueCallbacks}
											selectedCallback={selectedCallback}
											onSelect={(cb) => {
												setSelectedCallback(selectedCallback?.contactId === cb.contactId && selectedCallback?.callDateTime === cb.callDateTime ? null : cb);
												setContactTab(0);
											}}
											theme={theme}
											selectedIds={selectedIds}
											onToggleSelect={toggleSelect}
											getCallbackKey={getCallbackKey}
										/>
									)}
									{(statusFilter === 'all' || statusFilter === 'due') && (
										<CallbackSection
											title="Ready"
											count={readyCallbacks.length}
											color="success.main"
											expanded={expandedSections.ready}
											onToggle={() => toggleSection('ready')}
											callbacks={readyCallbacks}
											selectedCallback={selectedCallback}
											onSelect={(cb) => {
												setSelectedCallback(selectedCallback?.contactId === cb.contactId && selectedCallback?.callDateTime === cb.callDateTime ? null : cb);
												setContactTab(0);
											}}
											theme={theme}
											selectedIds={selectedIds}
											onToggleSelect={toggleSelect}
											getCallbackKey={getCallbackKey}
										/>
									)}
									{(statusFilter === 'all' || statusFilter === 'upcoming') && (
										<CallbackSection
											title="Upcoming"
											count={upcomingCallbacks.length}
											color="info.main"
											expanded={expandedSections.upcoming}
											onToggle={() => toggleSection('upcoming')}
											callbacks={upcomingCallbacks}
											selectedCallback={selectedCallback}
											onSelect={(cb) => {
												setSelectedCallback(selectedCallback?.contactId === cb.contactId && selectedCallback?.callDateTime === cb.callDateTime ? null : cb);
												setContactTab(0);
											}}
											theme={theme}
											selectedIds={selectedIds}
											onToggleSelect={toggleSelect}
											getCallbackKey={getCallbackKey}
										/>
									)}
								</Box>
							)}

							{/* ─── Selected callback detail panel ─── */}
							{selectedCallback && (
								<Card variant="outlined" sx={{ borderRadius: '10px', borderColor: 'primary.main' }}>
									<CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
										{/* Callback header */}
										<Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
											<Typography variant="subtitle2" fontWeight={600}>
												{new Date(selectedCallback.callDateTime).toLocaleString(undefined, {
													weekday: 'short',
													month: 'short',
													day: 'numeric',
													hour: 'numeric',
													minute: '2-digit',
												})}
											</Typography>
											<Chip
												label={getStatusInfo(selectedCallback).label}
												color={getStatusInfo(selectedCallback).color}
												variant={getStatusInfo(selectedCallback).variant}
												size="small"
												sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
											/>
										</Box>
										<Divider />

										{/* Callback info fields */}
										<Box sx={{ px: 2, py: 1.25, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
											<FieldItem label="Phone Number" value={selectedCallback.callBackPOC || '—'} />
											<FieldItem
												label="Assigned Agent"
												value={
													(selectedCallback.createdById === selectedCallback.agentId
														? selectedCallback.createdByAgentName
														: null) ||
													selectedCallback.agentId ||
													'Unassigned'
												}
											/>
											{selectedCallback.createdByAgentName && (
												<FieldItem label="Created By" value={selectedCallback.createdByAgentName} />
											)}
											{selectedCallback.note && (
												<FieldItem label="Note" value={selectedCallback.note} />
											)}
										</Box>

										{/* Contact details with tabs */}
										{contactData && (
											<>
												<Divider />
												<Box sx={{ px: 2, pt: 1.25 }}>
													<Tabs
														value={contactTab}
														onChange={(_, v) => setContactTab(v)}
														indicatorColor="primary"
														sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0.5, textTransform: 'none', fontSize: '0.8rem' } }}
													>
														<Tab label="Contact Info" value={0} />
														<Tab label="Custom Fields" value={1} />
														<Tab label="Activity" value={2} />
													</Tabs>
												</Box>
												<Box sx={{ px: 2, py: 1.25 }}>
													{contactTab === 0 && (
														<Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
															{(contactData.firstName || contactData.lastName) && (
																<FieldItem
																	label="Contact Name"
																	value={`${contactData.firstName || ''} ${contactData.lastName || ''}`.trim()}
																/>
															)}
															{contactData.externalId && <FieldItem label="External ID" value={contactData.externalId} />}
															{contactData.phone && <FieldItem label="Phone" value={contactData.phone} />}
															{contactData.cell && <FieldItem label="Cell" value={contactData.cell} />}
															{contactData.email && <FieldItem label="Email" value={contactData.email} />}
															{contactData.source && <FieldItem label="Source" value={contactData.source} />}
															{contactData.address1 && <FieldItem label="Address" value={contactData.address1} />}
															{contactData.city && <FieldItem label="City" value={contactData.city} />}
															{contactData.state && <FieldItem label="State" value={contactData.state} />}
															{contactData.zip && <FieldItem label="Zip" value={contactData.zip} />}
														</Box>
													)}
													{contactTab === 1 && (
														<Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
															{contactData.customFieldsList?.length > 0 ? (
																contactData.customFieldsList.map((field) => (
																	<FieldItem key={field.key} label={field.key} value={field.value ?? '—'} />
																))
															) : (
																<Typography variant="caption" color="text.secondary">
																	No custom fields
																</Typography>
															)}
														</Box>
													)}
													{contactTab === 2 && (
														<ActivityLog contactId={selectedCallback.contactId} />
													)}
												</Box>
											</>
										)}

										{!contactData && (
											<Box display="flex" justifyContent="center" py={2}>
												<CircularProgress size={18} />
											</Box>
										)}

										{/* Actions */}
										<Divider />
										<Box sx={{ px: 2, py: 1.25, display: 'flex', gap: 1 }}>
											{selectedCallback.agentId && (
												<Button
													variant="outlined"
													size="small"
													startIcon={releasing ? <CircularProgress size={14} /> : <LockOpenOutlined />}
													disabled={releasing}
													onClick={() => releaseCallBack(selectedCallback)}
													sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
												>
													{releasing ? 'Releasing...' : 'Release'}
												</Button>
											)}
											{!selectedCallback.agentId && (
												<Chip
													icon={<PersonOutlined sx={{ fontSize: 14 }} />}
													label="Unassigned"
													size="small"
													variant="outlined"
													sx={{ fontWeight: 600 }}
												/>
											)}
										</Box>
									</CardContent>
								</Card>
							)}

							{/* Empty state */}
							{filteredCallbacks.length === 0 && (
								<Paper
									variant="outlined"
									sx={{
										py: 4,
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										gap: 1,
										borderStyle: 'dashed',
										borderRadius: '12px',
									}}
								>
									<AccessTimeOutlined sx={{ fontSize: 36, color: 'text.disabled' }} />
									<Typography variant="body2" color="text.secondary">
										{callbacks.length === 0 ? 'No callbacks scheduled' : 'No callbacks match your filters'}
									</Typography>
								</Paper>
							)}
						</Box>
					)}
				</Box>
			</Box>
		</Drawer>
	);
}

// ─── List view collapsible section ──────────────────────────────────────────

function CallbackSection({ title, count, color, expanded, onToggle, callbacks, selectedCallback, onSelect, theme, selectedIds, onToggleSelect, getCallbackKey }) {
	return (
		<Box>
			<Box
				onClick={onToggle}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					cursor: 'pointer',
					py: 0.75,
					px: 1,
					borderRadius: '8px',
					'&:hover': { bgcolor: 'action.hover' },
				}}
			>
				{expanded ? <ExpandLess sx={{ fontSize: 18, color }} /> : <ExpandMore sx={{ fontSize: 18, color }} />}
				<Typography variant="subtitle2" fontWeight={600} color={color}>
					{title}
				</Typography>
				<Chip
					label={count}
					size="small"
					sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha(theme.palette.text.primary, 0.08) }}
				/>
			</Box>
			<Collapse in={expanded}>
				<Box sx={{ pl: 1, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
					{callbacks.length === 0 && (
						<Typography variant="caption" color="text.disabled" sx={{ pl: 1 }}>
							None
						</Typography>
					)}
					{callbacks.map((callback, idx) => {
						const isSelected =
							selectedCallback?.contactId === callback.contactId &&
							selectedCallback?.callDateTime === callback.callDateTime;
						const isChecked = selectedIds.has(getCallbackKey(callback));
						return (
							<Box
								key={`${callback.contactId}-${idx}`}
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 0.5,
								}}
							>
								{callback.agentId && (
									<Checkbox
										size="small"
										checked={isChecked}
										onChange={(e) => {
											e.stopPropagation();
											onToggleSelect(callback);
										}}
										onClick={(e) => e.stopPropagation()}
										sx={{ p: 0.25 }}
									/>
								)}
								{!callback.agentId && <Box sx={{ width: 28 }} />}
								<Box
									onClick={() => onSelect(callback)}
									sx={{
										flex: 1,
										px: 1.5,
										py: 0.75,
										borderRadius: '8px',
										cursor: 'pointer',
										border: '1px solid',
										borderColor: isSelected ? 'primary.main' : 'divider',
										bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
										transition: 'all 0.15s ease',
										'&:hover': { borderColor: 'primary.light', bgcolor: alpha(theme.palette.primary.main, 0.04) },
									}}
								>
									<Box display="flex" alignItems="center" justifyContent="space-between">
										<Typography variant="caption" fontWeight={600}>
											{new Date(callback.callDateTime).toLocaleString(undefined, {
												weekday: 'short',
												month: 'short',
												day: 'numeric',
												hour: 'numeric',
												minute: '2-digit',
											})}
										</Typography>
										{callback.agentId && (
											<Chip
												label={callback.createdByAgentName || callback.agentId}
												size="small"
												sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600 }}
											/>
										)}
									</Box>
									<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
										{callback.callBackPOC || 'No number'}
									</Typography>
								</Box>
							</Box>
						);
					})}
				</Box>
			</Collapse>
		</Box>
	);
}

/** Field item — colored label above the value */
function FieldItem({ label, value, color = 'primary.main' }) {
	return (
		<Box display="flex" flexDirection="column" gap={0.25}>
			<Typography variant="caption" color={color} fontWeight={600}>
				{label}
			</Typography>
			<Typography variant="body2" fontWeight={500} color="text.primary">
				{value}
			</Typography>
		</Box>
	);
}
